import type { AppStore } from 'store/types';
import { electronInstructionsApi } from './printing/renderer/ElectronInstructionsApi';
import { resolveUrls } from './instructionEndpoints';
import type { InstructionsIndex } from './printing/instructionsTypes';
import { fetchInstructionsIndex as fetchInstructionsIndexDefault } from './instructionsIndexFetcher';

interface BackgroundInstructionsServiceDeps {
  electronApi?: typeof electronInstructionsApi;
  fetchInstructionsIndex?: typeof fetchInstructionsIndexDefault;
}

class BackgroundInstructionsService {
  private readonly electronApi: typeof electronInstructionsApi;
  private readonly fetchInstructionsIndex: typeof fetchInstructionsIndexDefault;
  private store?: AppStore;
  private isInitialized = false;
  private refreshInterval?: number;
  private readonly REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

  constructor(deps: BackgroundInstructionsServiceDeps = {}) {
    this.electronApi = deps.electronApi ?? electronInstructionsApi;
    this.fetchInstructionsIndex =
      deps.fetchInstructionsIndex ?? fetchInstructionsIndexDefault;
  }

  async initialize(store: AppStore): Promise<void> {
    if (this.isInitialized) {
      console.warn('BackgroundInstructionsService already initialized');
      return;
    }

    this.store = store;

    try {
      console.log('Initializing BackgroundInstructionsService...');

      if (!(await this.electronApi.isAvailable())) {
        console.log(
          'Electron instructions API not available, skipping background preload',
        );
        return;
      }

      await this.prefetchAllInstructions();
      this.schedulePeriodicRefresh();

      this.isInitialized = true;
      console.log('BackgroundInstructionsService initialized successfully');
    } catch (error) {
      console.error('Error initializing BackgroundInstructionsService:', error);
    }
  }

  private getDispatch() {
    if (!this.store) {
      console.warn('BackgroundInstructionsService dispatch not available');
      return undefined;
    }
    return this.store.dispatch;
  }

  async prefetchAllInstructions(): Promise<void> {
    try {
      if (!(await this.electronApi.isAvailable())) {
        return;
      }

      const dispatch = this.getDispatch();
      if (!dispatch) {
        return;
      }

      console.log('Starting background prefetch of all instructions...');

      const instructionsIndex: InstructionsIndex =
        await this.fetchInstructionsIndex(dispatch);

      if (!instructionsIndex.length) {
        console.warn('No instructions index data received');
        return;
      }

      const baseUrl = import.meta.env.VITE_API_URL || '';
      const urls = resolveUrls(instructionsIndex, baseUrl);

      if (!urls.length) {
        console.log('No instruction files found to prefetch');
        return;
      }

      console.log(`Prefetching ${urls.length} instruction files in background...`);

      await this.electronApi.prefetchInBackground(urls);

      console.log('Background prefetch completed successfully');
    } catch (error) {
      console.error('Error prefetching all instructions:', error);
    }
  }

  schedulePeriodicRefresh(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }

    this.refreshInterval = window.setInterval(() => {
      console.log('Running periodic instructions refresh...');
      this.prefetchAllInstructions();
    }, this.REFRESH_INTERVAL_MS);

    console.log(`Scheduled periodic refresh every ${this.REFRESH_INTERVAL_MS / 1000 / 60} minutes`);
  }

  destroy(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = undefined;
    }
    this.store = undefined;
    this.isInitialized = false;
    console.log('BackgroundInstructionsService destroyed');
  }
}

export { BackgroundInstructionsService };
export const backgroundInstructionsService = new BackgroundInstructionsService();
